import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Compass, Coffee, CalendarDays, MapPin, Dumbbell, Leaf } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { IndiaMap } from "@/components/platform/IndiaMap";
import { DESTINATIONS, COLLECTIONS, EVENTS, EVENT_TYPES, EXPERIENCES, HERO_VIDEO, IMG } from "@/data/platform";
import { ARTICLES } from "@/data/journal";

const Section = ({ id, overline, title, sub, children, dark }) => (
  <section id={id} className={`py-20 md:py-28 px-6 md:px-12 scroll-mt-20 ${dark ? "bg-[#2D372B]" : ""}`}>
    <div className="max-w-7xl mx-auto">
      <div className="mb-12 md:mb-16 max-w-2xl">
        <div className={`overline mb-4 ${dark ? "!text-[#A9B39D]" : ""}`}>{overline}</div>
        <h2 className={`font-serif-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] ${dark ? "text-[#F7F5F0]" : "text-[#2D372B]"}`}>{title}</h2>
        {sub && <p className={`mt-4 leading-relaxed ${dark ? "text-[#C9CFC0]" : "text-[#5C665A]"}`}>{sub}</p>}
      </div>
      {children}
    </div>
  </section>
);

const DestCard = ({ d, big, wide }) => (
  <Link to={`/destinations/${d.slug}`} data-testid={`dest-card-${d.slug}`} className={`group relative rounded-3xl overflow-hidden block ${big ? "md:col-span-2 md:row-span-2" : ""} ${wide ? "md:col-span-2" : ""}`}>
    <div className={`${big ? "aspect-[4/3] md:aspect-auto md:h-full" : wide ? "aspect-[4/3] md:aspect-[8/2.95]" : "aspect-[4/3]"} overflow-hidden`}>
      <img src={d.image} alt={d.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-[#1A211A]/85 via-[#1A211A]/20 to-transparent" />
    <div className="absolute bottom-0 inset-x-0 p-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9CFC0] font-semibold">{d.region}</div>
      <h3 className={`font-serif-display text-[#F7F5F0] ${big ? "text-3xl md:text-4xl" : "text-2xl"}`}>{d.name}</h3>
      <p className={`text-sm text-[#C9CFC0] mt-1 ${big ? "" : "line-clamp-1"}`}>{d.tagline}</p>
    </div>
  </Link>
);

export default function PlatformHome() {
  const [month] = useState(new Date().getMonth() + 1);
  const monthEvents = EVENTS.filter((e) => e.month === month).slice(0, 3);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="top" className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />

      {/* Cinematic hero */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <video autoPlay muted loop playsInline poster={IMG.kerala} className="absolute inset-0 w-full h-full object-cover">
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A211A]/90 via-[#1A211A]/35 to-[#1A211A]/25" />
        <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 pt-32 md:pt-40 pb-20 md:pb-28">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9CFC0] font-semibold mb-5">India's Fitness, Wellness &amp; Specialty Coffee Travel Platform</div>
            <h1 data-testid="platform-hero-title" className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#F7F5F0] leading-[1.05] tracking-tight">
              Discover India's best fitness, wellness <span className="italic text-[#E8C9B8]">&amp; specialty coffee</span> experiences.
            </h1>
            <p className="mt-6 text-lg text-[#C9CFC0] max-w-xl leading-relaxed">
              Personalized journeys built around your fitness level, travel style and what genuinely excites you — not packages off a shelf.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/journey" data-testid="hero-start-planning" className="group inline-flex items-center gap-2 rounded-full px-8 py-4 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
                <Compass size={17} /> Start Planning My Journey
                <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href="#destinations" data-testid="hero-explore-india" className="inline-flex items-center gap-2 rounded-full px-8 py-4 border border-[#F7F5F0]/40 text-[#F7F5F0] font-medium hover:bg-[#F7F5F0]/10 transition-colors">
                Explore India
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Explore India */}
      <Section id="destinations" overline="Explore India" title={<>Ten places that <span className="italic text-[#AD6A48]">change people.</span></>} sub="Every destination guide covers fitness, wellness, coffee trails, adventure and where to eat well.">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <DestCard d={DESTINATIONS[1]} big />
          {DESTINATIONS.filter((_, i) => i !== 1).slice(0, 4).map((d) => <DestCard key={d.slug} d={d} />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
          {DESTINATIONS.filter((_, i) => i !== 1).slice(4).map((d, i) => <DestCard key={d.slug} d={d} wide={i === 3} />)}
        </div>
      </Section>

      {/* Journey Collections */}
      <Section id="collections" dark overline="Journey Collections" title={<span className="text-[#F7F5F0]">Travel by goal, <span className="italic text-[#E8C9B8]">not by brochure.</span></span>} sub="Curated lifestyle collections that combine destinations, training and experiences around who you are.">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {COLLECTIONS.map((c, i) => (
            <div key={c.slug} data-testid={`collection-card-${c.slug}`} className={`group rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm p-8 hover:border-[#C57B57]/50 transition-colors duration-300 ${i === 0 ? "lg:col-span-2 lg:flex lg:items-end lg:justify-between lg:gap-8" : ""}`}>
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#C57B57] font-semibold mb-3">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#F7F5F0]">{c.name}</h3>
                <p className="italic text-[#E8C9B8] font-serif-display mt-1">{c.tagline}</p>
                <p className="text-sm text-[#C9CFC0] mt-3 leading-relaxed">{c.blurb}</p>
                <ul className="mt-4 space-y-1.5">
                  {c.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-[#A9B39D]"><span className="h-1 w-1 rounded-full bg-[#C57B57] mt-2 shrink-0" />{h}</li>
                  ))}
                </ul>
                {c.slug === "executive" && (
                  <Link to="/retreats" data-testid="collection-retreats-cta" className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#C57B57] text-white text-sm font-medium hover:bg-[#AD6A48] transition-colors">
                    Plan a corporate retreat <ArrowUpRight size={14} />
                  </Link>
                )}
              </div>
              <div className="mt-5 flex flex-wrap gap-2 shrink-0">
                {c.destinations.map((s) => {
                  const d = DESTINATIONS.find((x) => x.slug === s);
                  return (
                    <Link key={s} to={`/destinations/${s}`} className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3.5 py-1.5 text-xs text-[#C9CFC0] hover:border-[#C57B57] hover:text-white transition-colors">
                      <MapPin size={11} /> {d?.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/journey" data-testid="collections-plan-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
            <Compass size={15} /> Build my own collection <ArrowRight size={15} />
          </Link>
        </div>
      </Section>

      {/* Popular experiences */}
      <Section overline="Popular Experiences" title={<>What travelers <span className="italic text-[#AD6A48]">book first.</span></>}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {EXPERIENCES.map((e) => (
            <Link key={e.name} to={`/destinations/${e.dest}`} data-testid={`experience-card-${e.dest}`} className="group relative rounded-3xl overflow-hidden">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={e.image} alt={e.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A211A]/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 p-5">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#E8C9B8] font-semibold">{e.tag}</span>
                <div className="font-serif-display text-lg sm:text-xl text-[#F7F5F0] leading-snug">{e.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Interactive map */}
      <Section id="map" overline="The Map" title={<>Where will India <span className="italic text-[#AD6A48]">take you?</span></>} sub="Tap a marker — every dot is a destination we know personally.">
        <IndiaMap />
      </Section>

      {/* Calendar teaser */}
      <Section dark overline="India Wellness Calendar" title={<span className="text-[#F7F5F0]">Travel with the <span className="italic text-[#E8C9B8]">seasons.</span></span>} sub="Marathons, yoga festivals, coffee harvests — plan journeys around the moments that matter.">
        <div className="grid md:grid-cols-3 gap-5">
          {monthEvents.map((e) => (
            <div key={e.name} data-testid="calendar-teaser-event" className="rounded-3xl border border-white/10 bg-white/5 p-7">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C57B57] font-semibold"><CalendarDays size={13} /> {EVENT_TYPES[e.type]} · This month</div>
              <h3 className="font-serif-display text-2xl text-[#F7F5F0] mt-3">{e.name}</h3>
              <p className="text-sm text-[#A9B39D] mt-1">{e.location} · {e.timing}</p>
              <p className="text-sm text-[#C9CFC0] mt-3 leading-relaxed">{e.note}</p>
            </div>
          ))}
        </div>
        <Link to="/calendar" data-testid="calendar-view-all" className="mt-10 inline-flex items-center gap-2 text-[#E8C9B8] font-medium hover:text-white transition-colors">
          View the full 12-month calendar <ArrowRight size={16} />
        </Link>
      </Section>

      {/* Coffee Explorer */}
      <section id="coffee" className="py-20 md:py-28 px-6 md:px-12 scroll-mt-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative rounded-3xl overflow-hidden">
            <img src={IMG.coorg} alt="Coffee estates of Coorg" loading="lazy" className="w-full aspect-[4/3] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A211A]/70 to-transparent" />
            <div className="absolute bottom-0 p-7">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E8C9B8] font-semibold">From these hills</div>
              <div className="font-serif-display text-2xl text-[#F7F5F0]">The Grounded Cup — our own beans</div>
            </div>
          </div>
          <div>
            <div className="overline mb-4">Coffee Explorer</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05]">Coffee isn't a stop on the trip. <span className="italic text-[#AD6A48]">It's a reason to go.</span></h2>
            <p className="mt-5 text-[#5C665A] leading-relaxed">Estate stays, harvest walks, cuppings with planters, roastery tours — woven into every journey. Then take the origin home: we roast and ship our own small-batch beans.</p>
            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              <Link to="/coffee/customize" data-testid="coffee-shop-cta" className="group rounded-2xl border border-[#E5E2D9] bg-white p-5 hover:border-[#AD6A48] transition-colors">
                <Coffee size={18} className="text-[#AD6A48] mb-2" />
                <div className="font-medium text-[#2D372B]">Shop our beans</div>
                <div className="text-xs text-[#5C665A] mt-0.5">Roasted to order, shipped worldwide</div>
              </Link>
              <Link to="/destinations/coorg" data-testid="coffee-trails-cta" className="group rounded-2xl border border-[#E5E2D9] bg-white p-5 hover:border-[#AD6A48] transition-colors">
                <MapPin size={18} className="text-[#AD6A48] mb-2" />
                <div className="font-medium text-[#2D372B]">Walk the coffee trails</div>
                <div className="text-xs text-[#5C665A] mt-0.5">Coorg estates, harvest Nov–Feb</div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Coach section */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-white border-y border-[#E5E2D9]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-4">The Human Behind It</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05]">Every journey is refined by <span className="italic text-[#AD6A48]">Coach Sanjay.</span></h2>
            <p className="mt-5 text-[#5C665A] leading-relaxed">12+ years of coaching bodies and minds — bootcamps, 1:1 training, nutrition and mental wellness. The AI drafts your journey; a coach who's actually run these trails makes it real.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/coaching" data-testid="coaching-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#89937C] text-white font-medium hover:bg-[#737D67] transition-colors">
                <Dumbbell size={15} /> Coaching &amp; Bootcamps
              </Link>
              <Link to="/train" data-testid="train-before-travel-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 border border-[#89937C] text-[#89937C] font-medium hover:bg-[#89937C] hover:text-white transition-colors">
                <Leaf size={15} /> Train before you travel — free plan
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={IMG.rishikesh} alt="Rishikesh" loading="lazy" className="rounded-3xl aspect-[3/4] object-cover mt-8" />
            <img src={IMG.ladakh} alt="Ladakh" loading="lazy" className="rounded-3xl aspect-[3/4] object-cover" />
          </div>
        </div>
      </section>

      {/* From the Journal */}
      <Section id="journal" overline="The Journal" title={<>Field notes from <span className="italic text-[#AD6A48]">active India.</span></>} sub="Guides, coffee stories and training blueprints — written to be used, not just read.">
        <div className="grid md:grid-cols-3 gap-5">
          {ARTICLES.slice(0, 3).map((a) => (
            <Link key={a.slug} to={`/journal/${a.slug}`} data-testid={`home-article-${a.slug}`} className="group bg-white border border-[#E5E2D9] rounded-3xl overflow-hidden hover:border-[#89937C] transition-colors flex flex-col">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#AD6A48]">{a.category}</span>
                <h3 className="font-serif-display text-xl mt-2 leading-snug flex-1">{a.title}</h3>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#89937C] group-hover:text-[#2D372B] transition-colors">Read <ArrowUpRight size={13} /></span>
              </div>
            </Link>
          ))}
        </div>
        <Link to="/journal" data-testid="journal-view-all" className="mt-10 inline-flex items-center gap-2 text-[#AD6A48] font-medium hover:text-[#8A5236] transition-colors">
          Browse the full Journal <ArrowRight size={16} />
        </Link>
      </Section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#2D372B] text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#F7F5F0] tracking-tight leading-[1.05]">Ready to build your <span className="italic text-[#E8C9B8]">wellness journey?</span></h2>
          <p className="mt-5 text-[#C9CFC0]">Nine questions. One personalized itinerary. Zero obligation.</p>
          <Link to="/journey" data-testid="final-cta-journey" className="mt-9 inline-flex items-center gap-2 rounded-full px-9 py-4 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
            <Compass size={17} /> Start Planning
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
